/**
 * Team — who is doing what, live (§16.3, ADR-118).
 *
 * This and "Agents" were the same screen twice: both rendered one card per
 * role, one adding pause, the other adding the current task and the
 * live output. Six roles appeared six times in two places, and neither answered
 * the question on its own. They are one screen now, and a role card carries
 * everything known about that role.
 *
 * A state shared by every role is still stated once above the grid; a card
 * earns detail only when it has something particular to say.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';

import type { OfficeAgent } from '@autonomous-team/ui-projections';

import { agentCardDetails, displaySpec, idleClaimsWithStaleSources, recoveryHint } from '../../logic/display-matrix.js';
import { LiveOutput } from '../components/live-output.js';
import { formatMicros, formatPercent, pluralize } from '../../logic/format.js';
import { useConsoleMutation, useTasks, useTeamStatus } from '../queries.js';
import { useStoreState } from '../store-context.js';
import type { LiveTranscript } from '../../store.js';
import { Chip, QueryPhase, Section } from '../components/state.js';
import { Button, Flash } from '@primer/react';

export function TeamScreen(): ReactNode {
  const scene = useStoreState((state) => state.scene);
  const transcripts = useStoreState((state) => state.transcripts);
  const status = useTeamStatus();
  const tasks = useTasks();

  const pauseTeam = useConsoleMutation<void, void>((data, context) => data.pauseTeam(context), [['team-status']]);
  const resumeTeam = useConsoleMutation<void, void>((data, context) => data.resumeTeam(context), [['team-status']]);
  const stopTeam = useConsoleMutation<void, void>(
    (data, context) => data.stopTeam('stopped from the Team screen', context),
    [['team-status'], ['roles']],
  );
  const teamPaused = status.data?.paused === true;
  const stopRole = useConsoleMutation<string, void>(
    (data, context, id) => data.stopRole(id, 'stopped from the Team screen', context),
    [['team-status'], ['roles']],
  );
  const [confirmStop, setConfirmStop] = useState<'team' | { roleInstanceId: string; name: string } | null>(null);

  if (scene.status !== 'ready') {
    return <QueryPhase isPending={scene.status === 'loading'} error={scene.status === 'error' ? scene.message : null} source="team projection" rows={5} />;
  }

  const value = scene.value;
  // A role reports the task binding it holds; a person recognises the card.
  const cards = new Map(
    (tasks.data ?? []).map((task) => [task.taskBindingId, { title: task.title, id: task.externalItemId }] as const),
  );
  const working = value.agents.filter((agent) => agent.state === 'working').length;
  const dishonest = idleClaimsWithStaleSources(value);
  const states = new Set(value.agents.map((agent) => agent.state));
  const shared = states.size === 1 ? value.agents[0] ?? null : null;

  return (
    <div className="stack">
      {dishonest.length > 0 ? (
        <Flash variant="danger" role="alert">
          <strong>Projection contract violated</strong>
          <p>
            {pluralize(dishonest.length, 'role')} reported as idle while holding an attempt that has gone quiet. AC-026
            forbids this; treat them as unknown.
          </p>
        </Flash>
      ) : null}

      {teamPaused ? (
        <Flash variant="warning">
          <strong>The team is paused.</strong> Nothing schedules and nothing new starts; work already in flight
          finishes on what it started with.
        </Flash>
      ) : null}

      <div className="team-controls">
        {teamPaused ? (
          <Button variant="primary" disabled={resumeTeam.isPending} onClick={() => resumeTeam.mutate(undefined)}>
            Resume team
          </Button>
        ) : (
          <>
            <Button disabled={pauseTeam.isPending} onClick={() => pauseTeam.mutate(undefined)}>
              Pause team
            </Button>
            <Button variant="danger" disabled={stopTeam.isPending} onClick={() => setConfirmStop('team')}>
              Stop now
            </Button>
          </>
        )}
      </div>

      {confirmStop === null ? null : (
        <StopConfirmDialog
          target={confirmStop}
          working={value.agents.filter((agent) => agent.state === 'working' || agent.state === 'delivering')}
          cards={cards}
          onConfirm={() => {
            if (confirmStop === 'team') stopTeam.mutate(undefined);
            else stopRole.mutate(confirmStop.roleInstanceId);
            setConfirmStop(null);
          }}
          onCancel={() => setConfirmStop(null)}
        />
      )}

      <section className="stat-strip" aria-label="Team at a glance">
        <Stat label="Working" value={working} caption={working === 0 ? 'Nobody is running a task' : 'roles running a task'} />
        <Stat label="Ready" value={value.readyShelfCount} caption="tasks an agent can pick up" />
        <Stat
          label="Waiting on a person"
          value={value.humanReviewCount}
          caption={value.humanReviewCount === 0 ? 'nothing is blocked on you' : 'open Human Review on the Board'}
          attention={value.humanReviewCount > 0}
        />
        <Stat label="Roles" value={value.agents.length} caption="configured in this team" />
      </section>

      <Section label="Roles" lede={shared?.stateExplanation}>
        <div className="card-grid">
          {value.agents.map((agent) => (
            <RoleCard
              agent={agent}
              transcript={transcripts.get(agent.roleInstanceId) ?? null}
              card={agent.currentTaskId === null ? null : cards.get(agent.currentTaskId) ?? null}
              onStop={() => setConfirmStop({ roleInstanceId: agent.roleInstanceId, name: agent.displayName })}
              key={agent.roleInstanceId}
            />
          ))}
        </div>
      </Section>

      {status.data !== undefined && status.data.incidents.length > 0 ? (
        <Section label="Incidents">
          <ul className="incident-list">
            {status.data.incidents.map((incident) => (
              <li className={`incident incident-${incident.severity}`} key={incident.id}>
                <div className="incident-head">
                  <Chip tone={incident.severity === 'critical' ? 'danger' : incident.severity === 'warning' ? 'wait' : 'info'} text={incident.severity} />
                  <h3>{incident.title}</h3>
                </div>
                <p>{incident.detail}</p>
                <p className="incident-action">{incident.recommendedAction}</p>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
    </div>
  );
}

function Stat({
  label,
  value,
  caption,
  attention = false,
}: {
  label: string;
  value: number;
  caption: string;
  attention?: boolean;
}): ReactNode {
  return (
    <div className={attention ? 'stat stat-attention' : 'stat'}>
      <p className="stat-label">{label}</p>
      <p className="stat-value">{value}</p>
      <p className="stat-caption">{caption}</p>
    </div>
  );
}

function RoleCard({
  agent,
  transcript,
  card,
  onStop,
}: {
  agent: OfficeAgent;
  transcript: LiveTranscript | null;
  /** The board card behind the held task binding, when one is known. */
  card: { title: string; id: string } | null;
  onStop: () => void;
}): ReactNode {
  const spec = displaySpec(agent.state);
  const needsAction = ['blocked', 'error', 'waiting-human', 'unknown'].includes(agent.state);
  const working = agent.state === 'working' || agent.state === 'delivering';
  // An idle card carries its name and its badge, and stops.
  const details = agent.state === 'idle' ? [] : agentCardDetails(agent).filter((line) => line.label !== 'Task');

  const pause = useConsoleMutation<string, void>((data, context, id) => data.pauseRole(id, context), [['team-status']]);
  const resume = useConsoleMutation<string, void>((data, context, id) => data.resumeRole(id, context), [['team-status']]);

  return (
    <article className={working ? `card card-${spec.tone} card-working` : `card card-${spec.tone}`}>
      <header className="card-head">
        <h3>
          <Link to={`/agents/${agent.roleInstanceId}`}>{agent.displayName}</Link>
        </h3>
        <Chip tone={spec.tone} text={spec.badgeText} label={agent.stateExplanation} />
      </header>

      {details.length === 0 ? null : (
        <dl className="detail-list">
          {details.map((line) => (
            <div key={line.label} style={{ display: 'contents' }}>
              <dt>{line.label}</dt>
              <dd>{line.value}</dd>
            </div>
          ))}
        </dl>
      )}

      {agent.currentTaskId === null ? null : (
        <p className="card-task">
          <span className="card-task-label">Working on</span>
          {card === null ? (
            <Link to={`/runs/${agent.currentTaskId}`}>this task</Link>
          ) : (
            <Link to={`/board/${card.id}`}>{card.title}</Link>
          )}
        </p>
      )}

      {/* What it is producing, while it produces it (ADR-114). */}
      {transcript === null && !working ? null : <LiveOutput transcript={transcript} working={working} />}

      {needsAction ? <p className="card-recovery">{recoveryHint(agent)}</p> : null}

      <div className="card-actions">
        <Button onClick={() => pause.mutate(agent.roleInstanceId)}>
          Pause role
        </Button>
        <Button onClick={() => resume.mutate(agent.roleInstanceId)}>
          Resume role
        </Button>
        <Button variant="danger" onClick={onStop}>
          Stop now
        </Button>
      </div>
    </article>
  );
}


/**
 * The confirmation Stop now deserves (spec §3): who stops, what they are in
 * the middle of, and the one thing that will NOT happen — no rollback of
 * repository or external changes. Cancel is the default posture.
 */
function StopConfirmDialog({
  target,
  working,
  cards,
  onConfirm,
  onCancel,
}: {
  target: 'team' | { roleInstanceId: string; name: string };
  working: readonly OfficeAgent[];
  cards: ReadonlyMap<string, { title: string; id: string }>;
  onConfirm: () => void;
  onCancel: () => void;
}): ReactNode {
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const affected =
    target === 'team' ? working : working.filter((agent) => agent.roleInstanceId === target.roleInstanceId);

  return (
    <dialog className="move-dialog" ref={dialogRef} onClose={onCancel} onCancel={onCancel}>
      <h3 className="move-dialog-title">
        {target === 'team' ? 'Stop the whole team now?' : `Stop «${target.name}» now?`}
      </h3>
      <p className="move-dialog-lede">
        This is Ctrl-C: running processes are interrupted (SIGINT, ten seconds, then SIGKILL) and nothing new
        starts until you press Resume.
      </p>
      {affected.length === 0 ? (
        <p className="move-dialog-lede">Nothing is running right now — the stop only locks new starts.</p>
      ) : (
        <ul className="stop-dialog-list">
          {affected.map((agent) => (
            <li key={agent.roleInstanceId}>
              <strong>{agent.displayName}</strong>
              {agent.currentTaskId === null
                ? ' — mid-run'
                : ` — working on «${cards.get(agent.currentTaskId)?.title ?? agent.currentTaskId}»`}
            </li>
          ))}
        </ul>
      )}
      <p className="move-dialog-lede">
        Repository changes, commits and anything already sent outside are NOT rolled back — the affected cards
        get a comment saying exactly what stopped and how to continue.
      </p>
      <div className="move-dialog-actions">
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="danger" onClick={onConfirm}>
          Stop now
        </Button>
      </div>
    </dialog>
  );
}
